<template>
    <div class="animated fadeIn crm-view-page">
        <div v-if="pageLoading" class="d-flex flex-column align-items-center justify-content-center py-5">
            <atom-spinner
                :animation-duration="1000"
                :size="60"
                color="#ff1d5e"
                class="my-5"
            />
            <p class="text-muted mb-0">Loading...</p>
        </div>
        <template v-else>
            <ComponentHeader
                title="CRM"
                :hasSearch="false"
                :hasCreate="false"
                :hasReload="false"
                :hasback="true"
                backButtonText="Back"
                @back-press="$router.push({ name: 'CRM' })"
            />

            <b-card class="card-wrap crm-hero-card" border-variant="info">
                <div class="crm-hero">
                    <div class="hero-left">
                        <span class="avatar" :title="account.initials">{{
                            account.initials
                        }}</span>
                        <div class="account-meta">
                            <div class="name-row">
                                <h1 class="account-name">
                                    {{ account.name }}
                                </h1>
                                <b-badge
                                    pill
                                    :class="[
                                        'status-badge',
                                        statusBadgeClass(account.status),
                                    ]"
                                >
                                    {{ account.status }}
                                </b-badge>
                                <span class="crm-id">CL-{{ account.id }}</span>
                            </div>
                            <div class="company-row">
                                <span class="company-name">{{
                                    account.company
                                }}</span>
                                <span class="meta-dot">·</span>
                                <b-badge
                                    pill
                                    variant="light"
                                    class="account-type-badge"
                                >
                                    CRM Account
                                </b-badge>
                            </div>
                        </div>
                    </div>

                    <div class="header-actions">
                        <b-button
                            variant="primary"
                            class="btn-upgrade"
                            @click="openUpgradeModal"
                        >
                            <i class="fa fa-arrow-up mr-1"></i>
                            Upgrade Plan
                        </b-button>
                        <b-button
                            variant="outline-secondary"
                            class="btn-ticket"
                            @click="openCreateTicketModal"
                        >
                            <i class="fa fa-ticket mr-1"></i>
                            Create Ticket
                        </b-button>
                        <b-dropdown
                            variant="outline-secondary"
                            right
                            no-caret
                            class="btn-dropdown-more"
                        >
                            <template #button-content>
                                <i class="fa fa-ellipsis-v"></i>
                            </template>

                            <b-dropdown-item href="#">
                                <i
                                    class="fa fa-envelope mr-2 text-primary"
                                ></i>
                                Send Email
                            </b-dropdown-item>

                            <b-dropdown-item href="#">
                                <i
                                    class="fa fa-sticky-note mr-2 text-info"
                                ></i>
                                Send Note
                            </b-dropdown-item>

                            <b-dropdown-item href="#">
                                <i class="fa fa-ban mr-2 text-danger"></i>
                                Suspend Account
                            </b-dropdown-item>
                        </b-dropdown>
                    </div>
                </div>
            </b-card>

            <b-card class="card-wrap crm-tabs-card" border-variant="info">
            <b-tabs v-model="tabIndex" class="crm-tabs" content-class="mt-0">
                <b-tab title="Overview" active>
                    <Overview
                        :account-id="account.id"
                        @upgrade="openUpgradeModal"
                        @show-invoices="tabIndex = 2"
                        @show-tickets="tabIndex = 4"
                        class="mt-2 p-2"
                    />
                </b-tab>
                <b-tab title="Subscription">
                    <Subscription
                        :account-id="account.id"
                        @upgrade="openUpgradeModal"
                        class="mt-2 p-2"
                    />
                </b-tab>
                <b-tab title="Invoices">
                    <Invoices :account-id="account.id" />
                </b-tab>
                <b-tab title="Payments">
                    <Payments :account-id="account.id" />
                </b-tab>
                <b-tab title="Tickets">
                    <Tickets
                        :account-id="account.id"
                        @create-ticket="openCreateTicketModal"
                        class="mt-2 p-2"
                    />
                </b-tab>
                <b-tab title="Notes & Activity">
                    <NotesActivity class="mt-2 p-2" :account-id="account.id" />
                </b-tab>
            </b-tabs>
            </b-card>

        <!-- Upgrade Plan Modal -->
        <b-modal
            v-model="showUpgradeModal"
            title="Change service plan"
            size="lg"
            centered
            modal-class="crm-modal crm-upgrade-modal"
            @hide="closeUpgradeModal"
        >
            <div class="upgrade-modal-body">
                <div class="billing-toggle-wrap">
                    <div class="billing-toggle-group">
                        <b-button
                            size="sm"
                            class="toggle-btn"
                            :variant="
                                billingCycle === 'monthly'
                                    ? 'primary'
                                    : 'outline-primary'
                            "
                            @click="billingCycle = 'monthly'"
                        >
                            Monthly
                        </b-button>
                        <b-button
                            size="sm"
                            class="toggle-btn"
                            :variant="
                                billingCycle === 'yearly'
                                    ? 'primary'
                                    : 'outline-primary'
                            "
                            @click="billingCycle = 'yearly'"
                        >
                            Yearly
                            <b-badge pill class="toggle-badge ml-2">
                                -20%
                            </b-badge>
                        </b-button>
                    </div>
                </div>

                <div class="plans-grid">
                    <div
                        v-for="plan in upgradePlans"
                        :key="plan.id"
                        :class="[
                            'plan-card',
                            { 'plan-current': isCurrentPlan(plan.id) },
                        ]"
                    >
                        <div v-if="plan.popular" class="plan-popular-badge">
                            MOST POPULAR
                        </div>
                        <div class="plan-name">{{ plan.name }}</div>
                        <div class="plan-price-wrap">
                            <span class="plan-price"
                                >${{ displayPrice(plan) }}</span
                            >
                            <span class="plan-period">/mo</span>
                        </div>
                        <ul class="plan-features">
                            <li v-for="(feat, i) in plan.features" :key="i">
                                <i class="fa fa-check-circle feature-icon"></i>
                                <span>{{ feat }}</span>
                            </li>
                        </ul>
                        <b-button
                            v-if="isCurrentPlan(plan.id)"
                            variant="outline-primary"
                            block
                            class="btn-plan-select"
                            disabled
                        >
                            CURRENT PLAN
                        </b-button>
                        <b-button
                            v-else
                            variant="outline-secondary"
                            block
                            class="btn-plan-select"
                            @click="selectPlan(plan)"
                        >
                            SELECT
                        </b-button>
                    </div>
                </div>

                <div class="proration-note">
                    <i class="fa fa-info-circle proration-icon"></i>
                    <div>
                        <strong>Proration Note:</strong>
                        Changing your plan mid-cycle will prorate the difference
                        to your next invoice. Credits or charges will appear
                        within 1–2 billing cycles.
                    </div>
                </div>
            </div>
            <template #modal-footer>
                <div class="crm-modal-actions">
                    <b-button variant="secondary" @click="closeUpgradeModal">
                        Cancel
                    </b-button>
                    <b-button variant="primary" @click="closeUpgradeModal">
                        OK
                    </b-button>
                </div>
            </template>
        </b-modal>

        <!-- Create Support Ticket Modal -->
        <b-modal
            v-model="showCreateTicketModal"
            title="Create support ticket"
            size="lg"
            centered
            scrollable
            modal-class="crm-modal crm-ticket-modal"
            @hide="closeCreateTicketModal"
        >
            <div class="ticket-modal-body">
                    <b-form id="create-ticket-form" @submit.prevent="submitTicket">
                        <b-form-group
                            label="ISSUE SUBJECT"
                            label-class="ticket-form-label"
                        >
                            <b-form-input
                                v-model="ticketForm.subject"
                                placeholder="Brief description of the issue"
                                class="ticket-input"
                            ></b-form-input>
                        </b-form-group>

                        <div class="ticket-form-row">
                            <b-form-group
                                label="ISSUE TYPE"
                                label-class="ticket-form-label"
                                class="ticket-form-half"
                            >
                                <b-form-select
                                    v-model="ticketForm.issueType"
                                    :options="issueTypeOptions"
                                    class="ticket-input"
                                ></b-form-select>
                            </b-form-group>
                            <b-form-group
                                label="CATEGORY"
                                label-class="ticket-form-label"
                                class="ticket-form-half"
                            >
                                <b-form-select
                                    v-model="ticketForm.category"
                                    :options="categoryOptions"
                                    class="ticket-input"
                                ></b-form-select>
                            </b-form-group>
                        </div>

                        <div class="ticket-form-row">
                            <b-form-group
                                label="PRIORITY (AUTO-SET)"
                                label-class="ticket-form-label"
                                class="ticket-form-half"
                            >
                                <div class="ticket-priority-badge">
                                    {{ ticketForm.priority }}
                                </div>
                            </b-form-group>
                            <b-form-group
                                label="ATTACHMENTS"
                                label-class="ticket-form-label"
                                class="ticket-form-half"
                            >
                                <input
                                    ref="ticketFileInput"
                                    type="file"
                                    accept="image/*"
                                    class="d-none"
                                    @change="onTicketFileChange"
                                />
                                <div
                                    class="ticket-upload-area"
                                    :class="{
                                        'ticket-uploading': ticketUploading,
                                    }"
                                    @click="triggerTicketFileInput"
                                >
                                    <span v-if="ticketUploading"
                                        >Uploading...</span
                                    >
                                    <span v-else>Click to upload</span>
                                </div>
                                <div
                                    v-if="
                                        ticketForm.attachments &&
                                        ticketForm.attachments.length
                                    "
                                    class="ticket-attachments-list mt-2"
                                >
                                    <div
                                        v-for="(
                                            url, idx
                                        ) in ticketForm.attachments"
                                        :key="idx"
                                        class="ticket-attachment-item"
                                    >
                                        <a
                                            :href="url"
                                            target="_blank"
                                            rel="noopener"
                                            class="ticket-attachment-link"
                                            >{{ fileNameFromUrl(url) }}</a
                                        >
                                    </div>
                                </div>
                            </b-form-group>
                        </div>

                        <b-form-group
                            label="DESCRIPTION"
                            label-class="ticket-form-label"
                        >
                            <b-form-textarea
                                v-model="ticketForm.description"
                                placeholder="Detailed explanation..."
                                rows="4"
                                class="ticket-input ticket-textarea"
                            ></b-form-textarea>
                        </b-form-group>
                    </b-form>
                </div>
            <template #modal-footer>
                <div class="crm-modal-actions">
                    <b-button
                        variant="outline-primary"
                        @click="closeCreateTicketModal"
                    >
                        Cancel
                    </b-button>
                    <b-button
                        variant="primary"
                        type="submit"
                        form="create-ticket-form"
                    >
                        Create Ticket
                    </b-button>
                </div>
            </template>
        </b-modal>
        </template>
    </div>
</template>

<script>
import ComponentHeader from "@/components/componentNewHeader";
import Overview from "@/views/channel/crm/components/Overview.vue";
import Subscription from "@/views/channel/crm/components/Subscription.vue";
import Invoices from "@/views/channel/crm/components/Invoices.vue";
import Payments from "@/views/channel/crm/components/Payments.vue";
import Tickets from "@/views/channel/crm/components/Tickets.vue";
import NotesActivity from "@/views/channel/crm/components/NotesActivity.vue";
import { AtomSpinner } from "epic-spinners";

export default {
    name: "CrmView",
    components: {
        ComponentHeader,
        Overview,
        Subscription,
        Invoices,
        Payments,
        Tickets,
        NotesActivity,
        AtomSpinner,
    },
    data() {
        return {
            pageLoading: false,
            tabIndex: 0,
            showUpgradeModal: false,
            showCreateTicketModal: false,
            billingCycle: "monthly",
            ticketForm: {
                subject: "",
                issueType: "Technical",
                category: "Technical Support",
                priority: "HIGH",
                description: "",
                attachments: [],
            },
            ticketUploading: false,
            issueTypeOptions: [
                { value: "Technical", text: "Technical" },
                { value: "Incident", text: "Incident" },
                { value: "Request", text: "Request" },
                { value: "Sales", text: "Sales" },
            ],
            categoryOptions: [
                { value: "Technical Support", text: "Technical Support" },
                { value: "Billing invoice", text: "Billing invoice" },
                { value: "Account management", text: "Account management" },
                { value: "Other", text: "Other" },
            ],
            account: {
                id: "9082",
                initials: "BI",
                name: "Biswarup Roy",
                company: "Calonex Solutions",
                status: "ACTIVE",
                planId: "enterprise",
            },
            currentPlanId: "enterprise",
            upgradePlans: [
                {
                    id: "basic",
                    name: "BASIC",
                    monthlyPrice: 159,
                    yearlyPricePerMo: 199,
                    features: ["5 Seats", "Basic Support", "10GB Storage"],
                },
                {
                    id: "pro",
                    name: "PRO",
                    monthlyPrice: 399,
                    yearlyPricePerMo: 499,
                    features: [
                        "10 Seats",
                        "Priority Support",
                        "50GB Storage",
                        "Advanced Security",
                    ],
                },
                {
                    id: "enterprise",
                    name: "ENTERPRISE",
                    monthlyPrice: 960,
                    yearlyPricePerMo: 1200,
                    features: [
                        "Unlimited Seats",
                        "24/7 Dedicated Support",
                        "Unlimited Storage",
                        "SSO & Custom Integration",
                    ],
                    popular: true,
                },
            ],
        };
    },
    created() {
        const id = this.$route.params.id;
        if (id) {
            // In future: fetch account by id; then set this.currentPlanId = account.planId
        }
        this.currentPlanId = this.account.planId || "enterprise";
    },
    methods: {
        statusBadgeClass(status) {
            const s = (status || "").toString().toLowerCase().trim();
            if (s === "active") return "badge-success";
            if (s === "trial") return "badge-info";
            if (s === "cancelled") return "badge-secondary";
            if (s === "past due" || s === "past-due") return "badge-danger";
            return "badge-secondary";
        },
        openUpgradeModal() {
            this.showUpgradeModal = true;
        },
        closeUpgradeModal() {
            this.showUpgradeModal = false;
        },
        selectPlan(plan) {
            console.log("Selected plan:", plan);
        },
        isCurrentPlan(planId) {
            return this.currentPlanId === planId;
        },
        displayPrice(plan) {
            if (this.billingCycle === "yearly") {
                return plan.yearlyPricePerMo.toLocaleString();
            }
            return plan.monthlyPrice.toLocaleString();
        },
        openCreateTicketModal() {
            this.showCreateTicketModal = true;
        },
        closeCreateTicketModal() {
            this.showCreateTicketModal = false;
            this.ticketForm.attachments = [];
        },
        triggerTicketFileInput() {
            if (this.ticketUploading) return;
            this.$refs.ticketFileInput && this.$refs.ticketFileInput.click();
        },
        onTicketFileChange(e) {
            const file = e.target.files && e.target.files[0];
            if (!file) return;
            if (!file.type.match("image.*")) {
                this.$toasted &&
                    this.$toasted.error("Please select an image file.");
                e.target.value = "";
                return;
            }
            const reader = new FileReader();
            reader.readAsDataURL(file);
            reader.onload = () => {
                this.uploadTicketAttachment(reader.result);
            };
            reader.onerror = () => {
                this.$toasted && this.$toasted.error("Failed to read file.");
            };
            e.target.value = "";
        },
        async uploadTicketAttachment(imageData) {
            this.ticketUploading = true;
            const dataobj = {
                image: imageData,
                key: (
                    Date.now().toString(36) +
                    Math.random().toString(36).substr(2, 5)
                ).toLowerCase(),
                isPublic: true,
                replaceExisting: true,
                basePath: "crm",
            };
            try {
                const res = await this.$store.dispatch(
                    "inventory/uploadImageToStorage",
                    dataobj,
                );
                if (res && res.data && res.data.fileUrl) {
                    this.ticketForm.attachments =
                        this.ticketForm.attachments || [];
                    this.ticketForm.attachments.push(res.data.fileUrl);
                    if (this.$toasted) this.$toasted.success("File uploaded.");
                }
            } catch (err) {
                if (this.$toasted)
                    this.$toasted.error("Upload failed. Please try again.");
            } finally {
                this.ticketUploading = false;
            }
        },
        fileNameFromUrl(url) {
            if (!url) return "Attachment";
            const parts = url.split("/");
            return parts[parts.length - 1] || "Attachment";
        },
        submitTicket() {
            console.log("Ticket submitted", this.ticketForm);
        },
    },
};
</script>

<style scoped>
.crm-view-page {
    min-height: 100%;
    overflow-x: hidden;
    max-width: 100%;
}

.card-wrap {
    box-shadow: 0 0 10px rgba(0, 0, 0, 0.08);
    border-radius: 12px;
}

.crm-hero-card {
    margin-top: 12px;
}

.crm-hero {
    display: flex;
    flex-wrap: wrap;
    align-items: flex-start;
    justify-content: space-between;
    gap: 16px;
}

.hero-left {
    display: flex;
    align-items: center;
    flex: 1 1 auto;
    min-width: 0;
}

.crm-tabs-card {
    margin-top: 12px;
}

/* Tabs (match Build Generation: keep default bootstrap tabs, minimal overrides) */
.crm-tabs >>> .tab-pane {
    padding: 0;
}

.avatar {
    width: 48px;
    height: 48px;
    border-radius: 50%;
    background: #1565c0;
    color: #fff;
    font-size: 1rem;
    font-weight: 700;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
}

.account-meta {
    margin-left: 16px;
    min-width: 0;
}

.account-name {
    font-size: 1.5rem;
    font-weight: 700;
    color: var(--slate-900, #0f172a);
    margin: 0;
    display: inline;
}

.name-row {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 4px;
}

.crm-id {
    font-size: 0.9rem;
    color: var(--slate-500, #64748b);
    font-weight: 600;
}

.company-row {
    margin-top: 4px;
}

.company-name {
    color: var(--slate-900, #0f172a);
}

.meta-dot {
    color: #adb5bd;
    margin: 0 6px;
}

.account-type-badge {
    color: var(--slate-700, #334155);
    font-weight: 600;
}

.header-actions {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 10px;
    flex-shrink: 0;
}

.btn-upgrade {
    border-radius: 10px;
    font-weight: 600;
}

.btn-ticket {
    border-radius: 10px;
}

.btn-dropdown-more {
    border-radius: 10px;
}

/* Modals (standard) */
::v-deep .crm-modal .modal-content {
    border-radius: 12px;
    border: 1px solid var(--slate-200, #e2e8f0);
    box-shadow: 0 8px 32px rgba(0, 0, 0, 0.15);
}

::v-deep .crm-modal .modal-header {
    border-bottom: 1px solid var(--slate-200, #e2e8f0);
    padding: 14px 18px;
}

::v-deep .crm-modal .modal-title {
    font-weight: 800;
    letter-spacing: 0.01em;
}

::v-deep .crm-modal .modal-body {
    padding: 18px 18px 8px;
}

::v-deep .crm-modal .modal-footer {
    border-top: 1px solid var(--slate-200, #e2e8f0);
    padding: 12px 18px 16px;
}

::v-deep .crm-upgrade-modal .modal-dialog {
    max-width: 900px;
}

::v-deep .crm-ticket-modal .modal-dialog {
    max-width: 640px;
}

.crm-modal-actions {
    display: flex;
    justify-content: center;
    gap: 10px;
    width: 100%;
}

.crm-modal-actions .btn {
    min-width: 160px;
    border-radius: 10px;
    font-weight: 700;
}

.upgrade-modal-body {
    padding: 0;
}

.billing-toggle-wrap {
    display: flex;
    justify-content: center;
    margin-bottom: 17px;
}

.billing-toggle-group {
    display: flex;
    gap: 0;
}

.billing-toggle-group .toggle-btn {
    border-radius: 0;
    box-shadow: none;
    outline: none;
    display: inline-flex;
    align-items: center;
    justify-content: center;
}

.billing-toggle-group .toggle-btn:first-child {
    border-radius: 8px 0 0 8px;
}

.billing-toggle-group .toggle-btn:last-child {
    border-radius: 0 8px 8px 0;
}

.billing-toggle-group .toggle-btn:focus,
.billing-toggle-group .toggle-btn:active {
    box-shadow: none !important;
}

.toggle-badge {
    background-color: #28a745 !important;
    color: #fff !important;
    font-size: 0.65rem;
}

.plans-grid {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 20px;
    margin-bottom: 24px;
}

.plan-card {
    background: #fff;
    border: 1px solid #e9ecef;
    border-radius: 12px;
    padding: 24px;
    position: relative;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.06);
}

.plan-card.plan-current {
    border-color: #1565c0;
    border-width: 2px;
}

.plan-popular-badge {
    position: absolute;
    top: -12px;
    left: 50%;
    transform: translateX(-50%);
    background: #1565c0;
    color: #fff;
    padding: 6px 14px;
    border-radius: 16px;
    font-weight: 800;
    font-size: 0.75rem;
    letter-spacing: 0.03em;
    z-index: 2;
}

.plan-name {
    font-size: 0.75rem;
    font-weight: 600;
    letter-spacing: 0.05em;
    color: #6c757d;
    margin-bottom: 12px;
}

.plan-price-wrap {
    margin-bottom: 20px;
}

.plan-price {
    font-size: 1.75rem;
    font-weight: 700;
    color: #1a1d21;
}

.plan-period {
    font-size: 0.9rem;
    color: #6c757d;
    margin-left: 2px;
}

.plan-features {
    list-style: none;
    padding: 0;
    margin: 0 0 24px 0;
}

.plan-features li {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 6px 0;
    font-size: 0.9rem;
    color: #1a1d21;
}

.feature-icon {
    color: #28a745;
    flex-shrink: 0;
}

.btn-plan-select {
    border-radius: 8px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.03em;
}

.proration-note {
    display: flex;
    align-items: flex-start;
    gap: 12px;
    background: #fffbea;
    border: 1px solid #f5f0d8;
    border-radius: 8px;
    padding: 16px;
    flex-shrink: 0;
}

.proration-icon {
    color: #b8860b;
    font-size: 1.25rem;
    flex-shrink: 0;
    margin-top: 2px;
}

.proration-note div {
    font-size: 0.9rem;
    color: #1a1d21;
    line-height: 1.5;
}

/* Create Support Ticket Modal */
.ticket-modal-body {
    padding: 0;
}

.ticket-form-label {
    font-size: 0.7rem !important;
    font-weight: 600 !important;
    letter-spacing: 0.05em !important;
    color: #6c757d !important;
    text-transform: uppercase;
    margin-bottom: 8px !important;
}

.ticket-form-row {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 24px;
}

.ticket-form-half {
    margin-bottom: 0;
}

.ticket-input {
    border-radius: 8px;
    background: #f8f9fa;
    border: 1px solid #e9ecef;
}

.ticket-input:focus {
    background: #fff;
    border-color: #6f42c1;
    box-shadow: 0 0 0 0.2rem rgba(111, 66, 193, 0.25);
}

.ticket-textarea {
    min-height: 100px;
}

.ticket-priority-badge {
    display: inline-flex;
    align-items: center;
    padding: 10px 16px;
    border-radius: 8px;
    background: #fff3e6;
    color: #c2410c;
    font-weight: 700;
    font-size: 0.9rem;
    text-transform: uppercase;
}

.ticket-upload-area {
    border: 2px dashed #dee2e6;
    border-radius: 8px;
    background: #f8f9fa;
    padding: 24px;
    text-align: center;
    color: #6c757d;
    font-size: 0.9rem;
    cursor: pointer;
}

.ticket-upload-area:hover {
    border-color: #6f42c1;
    background: #f5f0fa;
}

.ticket-upload-area.ticket-uploading {
    pointer-events: none;
    opacity: 0.8;
}

.ticket-attachments-list {
    display: flex;
    flex-direction: column;
    gap: 4px;
}

.ticket-attachment-item {
    font-size: 0.85rem;
}

.ticket-attachment-link {
    color: #6f42c1;
}

.ticket-attachment-link:hover {
    text-decoration: underline;
}

.ticket-modal-body >>> .form-group {
    margin-bottom: 24px;
}

@media (max-width: 768px) {
    .ticket-form-row {
        grid-template-columns: 1fr;
    }
}

@media (max-width: 768px) {
    .plans-grid {
        grid-template-columns: 1fr;
    }
}

@media (max-width: 768px) {
    .header-actions {
        width: 100%;
        display: flex;
        gap: 8px;
        flex-wrap: nowrap;
        align-items: stretch;
    }

    .header-actions .btn-upgrade,
    .header-actions .btn-ticket {
        flex: 1;
        min-width: 0;
        margin-right: 0 !important;
        font-size: 0.8rem;
        padding: 0.4rem 0.5rem;
    }

    .header-actions .btn-dropdown-more {
        flex-shrink: 0;
        width: auto;
        min-width: 44px;
    }

    .header-actions >>> .btn-dropdown-more,
    .header-actions >>> .dropdown {
        display: flex;
        flex-shrink: 0;
    }
}

@media (max-width: 480px) {
    .header-actions {
        flex-direction: column;
    }

    .header-actions .btn-upgrade,
    .header-actions .btn-ticket {
        flex: none;
        width: 100%;
    }

    .header-actions .btn-dropdown-more {
        width: 100%;
        display: flex;
        justify-content: center;
    }
}
</style>
